/**
 * Terminal 配置文件
 * ============================================================
 * 别人可以通过修改这个文件来自定义登录页 Terminal 的输出内容：
 *   - logo        : neofetch 的 ASCII art logo（多行字符串数组）
 *   - staticInfo  : 静态的系统信息（OS / Host / Kernel / Shell 等）
 *   - bootLines   : 终端启动时显示的欢迎语
 *   - brand       : 品牌信息（标题栏文字 / 应用名）
 *
 * 动态数据（浏览器版本 / 平台 / 分屏率 / 当前时间）由 systemInfo.ts
 * 在运行时从 navigator / window 自动获取，无需在此配置。
 * ============================================================
 */

export interface TerminalConfig {
	/** 品牌名（用于标题栏 + 命令 whoami 输出） */
	brand: {
		appName: string
		hostname: string
		user: string
	}
	/** 版本信息 */
	version: {
		app: string
		build: string
		kernel: string
		semi: string
	}
	/** neofetch 输出的 ASCII art logo（每行一项） */
	logo: string[]
	/** 启动时显示的欢迎/系统信息（每行一项，type 决定渲染样式） */
	bootLines: Array<{
		text: string
		type?: 'system' | 'output' | 'command' | 'error'
	}>
	/** neofetch 中的静态信息行（动态数据 browser/platform/time 由运行时补充） */
	staticInfo: Array<{
		key: string
		value: string
		/** 是否用 primary 色突出（高亮显示） */
		highlight?: boolean
	}>
	/** 底部主题色块配置（方块字符 + 颜色名） */
	palette: Array<{
		block: string
		label: string
	}>
}

/**
 * 默认配置 —— 其他人可以 fork 后修改这里的值
 */
export const terminalConfig: TerminalConfig = {
	brand: {
		appName: 'Plus One L-CRM',
		hostname: 'l-crm',
		user: 'crm'
	},
	version: {
		app: 'Plus One L-CRM v1.0.0 (线索管理系统 · build 2024.11)',
		build: '2024.11',
		kernel: 'React 18.3.1',
		semi: 'Semi Design 2.99'
	},
	// ASCII art logo —— figlet colossal 字体 "PLUS ONE"（21 行 × ~150 字符）
	// 终端会自动横向滚动显示完整 logo
	logo: [
		'          _____                    _____     _____                    _____                   _______                   _____                    _____          ',
		'         /\\    \\                  /\\    \\   /\\    \\                  /\\    \\                 /::\\    \\                 /\\    \\                  /\\    \\         ',
		'        /::\\    \\                /::\\____\\ /::\\____\\                /::\\    \\               /::::\\    \\               /::\\____\\                /::\\    \\        ',
		'       /::::\\    \\              /:::/    //:::/    /               /::::\\    \\             /::::::\\    \\             /::::|   |               /::::\\    \\       ',
		'      /::::::\\    \\            /:::/    //:::/    /               /::::::\\    \\           /::::::::\\    \\           /:::::|   |              /::::::\\    \\      ',
		'     /:::/\\:::\\    \\          /:::/    //:::/    /               /:::/\\:::\\    \\         /:::/~~\\:::\\    \\         /::::::|   |             /:::/\\:::\\    \\     ',
		'    /:::/__\\:::\\    \\        /:::/    //:::/    /               /:::/__\\:::\\    \\       /:::/    \\:::\\    \\       /:::/|::|   |            /:::/__\\:::\\    \\    ',
		'   /::::\\   \\:::\\    \\      /:::/    //:::/    /                \\:::\\   \\:::\\    \\     /:::/    / \\:::\\    \\     /:::/ |::|   |           /::::\\   \\:::\\    \\   ',
		'  /::::::\\   \\:::\\    \\    /:::/    //:::/    /      _____    ___\\:::\\   \\:::\\    \\   /:::/____/   \\:::\\____\\   /:::/  |::|   | _____    /::::::\\   \\:::\\    \\  ',
		' /:::/\\:::\\   \\:::\\____\\  /:::/    //:::/____/      /\\    \\  /\\   \\:::\\   \\:::\\    \\ |:::|    |     |:::|    | /:::/   |::|   |/\\    \\  /:::/\\:::\\   \\:::\\    \\ ',
		'/:::/  \\:::\\   \\:::|    |/:::/____/|:::|    /      /::\\____\\/::\\   \\:::\\   \\:::\\____\\|:::|____|     |:::|    |/:: /    |::|   /::\\____\\/:::/__\\:::\\   \\:::\\____\\',
		'\\::/    \\:::\\  /:::|____|\\:::\\    \\|:::|____\\     /:::/    /\\:::\\   \\:::\\   \\::/    / \\:::\\    \\   /:::/    / \\::/    /|::|  /:::/    /\\:::\\   \\:::\\   \\::/    / ',
		' \\/_____/\\:::\\/:::/    /  \\:::\\    \\\\:::\\    \\   /:::/    /  \\:::\\   \\:::\\   \\/____/   \\:::\\    \\ /:::/    /   \\/____/ |::| /:::/    /  \\:::\\   \\:::\\   \\/____/ ',
		'          \\::::::/    /    \\:::\\    \\\\:::\\    \\ /:::/    /    \\:::\\   \\:::\\    \\        \\:::\\    /:::/    /            |::|/:::/    /    \\:::\\   \\:::\\    \\     ',
		'           \\::::/    /      \\:::\\    \\\\:::\\    /:::/    /      \\:::\\   \\:::\\____\\        \\:::\\__/:::/    /             |::::::/    /      \\:::\\   \\:::\\____\\    ',
		'            \\::/____/        \\:::\\    \\\\:::\\__/:::/    /        \\:::\\  /:::/    /         \\::::::::/    /              |:::::/    /        \\:::\\   \\::/    /    ',
		'             ~~               \\:::\\    \\\\::::::::/    /          \\:::\\/:::/    /           \\::::::/    /               |::::/    /          \\:::\\   \\/____/     ',
		'                               \\:::\\    \\\\::::::/    /            \\::::::/    /             \\::::/    /                /:::/    /            \\:::\\    \\         ',
		'                                \\:::\\____\\\\::::/    /              \\::::/    /               \\::/____/                /:::/    /              \\:::\\____\\        ',
		'                                 \\::/    / \\::/____/                \\::/    /                 ~~                      \\::/    /                \\::/    /        ',
		'                                  \\/____/   ~~                       \\/____/                                           \\/____/                  \\/____/         '
	],
	// 启动时显示的 boot 信息
	bootLines: [
		{ text: 'Plus One L-CRM Terminal v1.0.0 — 线索管理系统', type: 'system' },
		{ text: 'Type "help" for available commands.', type: 'system' }
	],
	// neofetch 中的静态系统信息（动态部分：browser/platform/time/runtime 等不写在这里）
	staticInfo: [
		{ key: 'Module', value: '线索管理 / Lead Management', highlight: true },
		{ key: 'OS', value: '', highlight: true }, // 由 runtime 填充
		{ key: 'Host', value: '' },
		{ key: 'Kernel', value: '' },
		{ key: 'Uptime', value: 'just booted' },
		{ key: 'Shell', value: 'semi-design' },
		{ key: 'Resolution', value: '' }, // runtime
		{ key: 'DE', value: 'Semi Design' },
		{ key: 'Browser', value: '', highlight: true }, // runtime
		{ key: 'Platform', value: '', highlight: true }, // runtime
		{ key: 'Locale', value: '' }, // runtime
		{ key: 'CPU Cores', value: '' }, // runtime
		{ key: 'Memory', value: '' }, // runtime
		{ key: 'Date', value: '' } // runtime
	],
	// 底部主题色块
	palette: [
		{ block: '███', label: 'indigo' },
		{ block: '███', label: 'violet' },
		{ block: '███', label: 'cyan' },
		{ block: '███', label: 'blue' }
	]
}
